import React from 'react';
import { Code, Brain, Zap, Cloud, Database, Shield, Smartphone, Globe } from 'lucide-react';
import { motion } from 'framer-motion';
import Tilt from 'react-parallax-tilt';

const Products: React.FC = () => {
  const productCategories = [
    {
      name: 'AI & Machine Learning Solutions',
      description: 'Intelligent systems that learn and adapt to your business needs',
      items: ['Predictive Analytics', 'Natural Language Processing', 'Computer Vision', 'Automated Decision Making'],
      icon: Brain,
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      name: 'Automation Platforms',
      description: 'Streamline operations with intelligent automation tools',
      items: ['Workflow Automation', 'Process Optimization', 'Task Scheduling', 'Integration Systems'],
      icon: Zap,
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      name: 'Web & Mobile Applications',
      description: 'Custom applications tailored to your business requirements',
      items: ['Progressive Web Apps', 'Mobile Applications', 'E-commerce Platforms', 'Content Management'],
      icon: Smartphone,
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      name: 'Cloud & Infrastructure',
      description: 'Scalable cloud solutions for modern businesses',
      items: ['Cloud Migration', 'DevOps Solutions', 'Microservices Architecture', 'Container Orchestration'],
      icon: Cloud,
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pt-20">
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-br from-primary-400/20 to-purple-600/20 dark:from-cyan-400/10 dark:to-purple-600/10 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          <motion.div
            className="absolute bottom-20 left-20 w-72 h-72 bg-gradient-to-tr from-cyan-400/20 to-blue-600/20 dark:from-cyan-400/10 dark:to-blue-600/10 rounded-full blur-3xl"
            animate={{
              scale: [1.2, 1, 1.2],
              rotate: [360, 180, 0],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="text-5xl md:text-7xl font-bold mb-8 bg-gradient-to-r from-gray-900 via-primary-600 to-purple-600 dark:from-white dark:via-cyan-400 dark:to-purple-400 bg-clip-text text-transparent"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              Our Products
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              Discover our comprehensive suite of technology solutions designed to transform your business 
              through innovation, automation, and intelligent systems.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Technology Solutions Overview */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                Technology Solutions That Drive Results
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                At Lunexa, we develop cutting-edge technology products that solve real business challenges. 
                Our solutions combine the latest in AI, automation, and cloud technologies to deliver 
                measurable results for our clients.
              </p>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                From intelligent automation platforms to custom AI solutions, our products are designed 
                to scale with your business and adapt to your evolving needs.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <motion.div 
                  className="backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-2xl p-6 text-center shadow-glass"
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-cyan-400 dark:to-purple-400 bg-clip-text text-transparent">100+</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Solutions Deployed</div>
                </motion.div>
                <motion.div 
                  className="backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-2xl p-6 text-center shadow-glass"
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 dark:from-green-400 dark:to-emerald-400 bg-clip-text text-transparent">50+</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Happy Clients</div>
                </motion.div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Tilt
                tiltMaxAngleX={5}
                tiltMaxAngleY={5}
                perspective={1000}
                scale={1.02}
              >
                <div className="backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-3xl p-8 shadow-glass">
                  <h3 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Key Achievements</h3>
                  <div className="space-y-6">
                    <motion.div 
                      className="flex items-center"
                      whileHover={{ x: 10 }}
                    >
                      <div className="p-3 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl mr-4">
                        <Database className="text-white" size={24} />
                      </div>
                      <span className="text-gray-700 dark:text-gray-300">99.9% System Uptime Across All Products</span>
                    </motion.div>
                    <motion.div 
                      className="flex items-center"
                      whileHover={{ x: 10 }}
                    >
                      <div className="p-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl mr-4">
                        <Globe className="text-white" size={24} />
                      </div>
                      <span className="text-gray-700 dark:text-gray-300">Global Deployment Across 25+ Countries</span>
                    </motion.div>
                    <motion.div 
                      className="flex items-center"
                      whileHover={{ x: 10 }}
                    >
                      <div className="p-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl mr-4">
                        <Shield className="text-white" size={24} />
                      </div>
                      <span className="text-gray-700 dark:text-gray-300">Enterprise-Grade Security Standards</span>
                    </motion.div>
                    <motion.div 
                      className="flex items-center"
                      whileHover={{ x: 10 }}
                    >
                      <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl mr-4">
                        <Code className="text-white" size={24} />
                      </div>
                      <span className="text-gray-700 dark:text-gray-300">Continuous Innovation & Updates</span>
                    </motion.div>
                  </div>
                </div>
              </Tilt>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-32 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600/5 via-purple-600/5 to-cyan-600/5 dark:from-primary-600/3 dark:via-purple-600/3 dark:to-cyan-600/3" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              Technology Product Suite
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Comprehensive solutions designed to accelerate your digital transformation journey
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {productCategories.map((category, index) => {
              const Icon = category.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Tilt
                    tiltMaxAngleX={10}
                    tiltMaxAngleY={10}
                    perspective={1000}
                    scale={1.02}
                  >
                    <div className="group h-full backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-3xl p-8 shadow-glass hover:shadow-glass-dark transition-all duration-500">
                      {/* Gradient Border */}
                      <div className={`absolute inset-0 bg-gradient-to-r ${category.gradient} opacity-0 group-hover:opacity-20 rounded-3xl transition-opacity duration-500`} />
                      
                      {/* Icon */}
                      <motion.div
                        className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${category.gradient} mb-6 shadow-lg`}
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ type: "spring", stiffness: 400 }}
                      >
                        <Icon className="w-8 h-8 text-white" />
                      </motion.div>

                      <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-primary-600 group-hover:to-purple-600 dark:group-hover:from-cyan-400 dark:group-hover:to-purple-400 transition-all duration-300">
                        {category.name}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                        {category.description}
                      </p>
                      
                      <div className="grid grid-cols-1 gap-3">
                        {category.items.map((item, itemIndex) => (
                          <motion.div 
                            key={itemIndex} 
                            className="flex items-center text-sm text-gray-700 dark:text-gray-300"
                            whileHover={{ x: 5 }}
                          >
                            <div className={`w-2 h-2 bg-gradient-to-r ${category.gradient} rounded-full mr-3`}></div>
                            {item}
                          </motion.div>
                        ))}
                      </div>

                      {/* Hover Effect */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl"
                      />
                    </div>
                  </Tilt>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Deployment Options */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              Flexible Deployment Options
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Choose the deployment model that best fits your business requirements and infrastructure
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Tilt
                tiltMaxAngleX={10}
                tiltMaxAngleY={10}
                perspective={1000}
                scale={1.02}
              >
                <div className="group text-center h-full backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-3xl p-8 shadow-glass hover:shadow-glass-dark transition-all duration-500">
                  <motion.div
                    className="inline-flex p-4 rounded-2xl bg-gradient-to-r from-blue-500 to-cyan-500 mb-6 shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <Cloud className="w-8 h-8 text-white" />
                  </motion.div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Cloud-Native</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">Fully managed cloud solutions with automatic scaling and updates</p>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                    <li>• Zero infrastructure management</li>
                    <li>• Automatic scaling</li>
                    <li>• Global availability</li>
                    <li>• Pay-as-you-use pricing</li>
                  </ul>
                </div>
              </Tilt>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Tilt
                tiltMaxAngleX={10}
                tiltMaxAngleY={10}
                perspective={1000}
                scale={1.02}
              >
                <div className="group text-center h-full backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-3xl p-8 shadow-glass hover:shadow-glass-dark transition-all duration-500">
                  <motion.div
                    className="inline-flex p-4 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 mb-6 shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <Shield className="w-8 h-8 text-white" />
                  </motion.div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">On-Premises</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">Complete control with on-site deployment for maximum security</p>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                    <li>• Full data control</li>
                    <li>• Custom configurations</li>
                    <li>• Enhanced security</li>
                    <li>• Compliance ready</li>
                  </ul>
                </div>
              </Tilt>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Tilt
                tiltMaxAngleX={10}
                tiltMaxAngleY={10}
                perspective={1000}
                scale={1.02}
              >
                <div className="group text-center h-full backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-3xl p-8 shadow-glass hover:shadow-glass-dark transition-all duration-500">
                  <motion.div
                    className="inline-flex p-4 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500 mb-6 shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <Database className="w-8 h-8 text-white" />
                  </motion.div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Hybrid</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">Best of both worlds with flexible hybrid deployment options</p>
                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                    <li>• Flexible architecture</li>
                    <li>• Gradual migration</li>
                    <li>• Cost optimization</li>
                    <li>• Risk mitigation</li>
                  </ul>
                </div>
              </Tilt>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Development Approach */}
      <section className="py-32 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-purple-600 to-cyan-600 dark:from-primary-700 dark:via-purple-700 dark:to-cyan-700" />
        <div className="absolute inset-0 bg-black/20 dark:bg-black/40" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Our Development Philosophy
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              How we ensure quality, innovation, and customer success in every product we build
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="bg-white/20 backdrop-blur-sm w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg"
                whileHover={{ scale: 1.1, rotate: 5 }}
              >
                <Code className="text-white" size={40} />
              </motion.div>
              <h3 className="text-2xl font-bold mb-4">Innovation First</h3>
              <p className="text-white/80 leading-relaxed">
                We leverage cutting-edge technologies and methodologies to create solutions that push the boundaries of what's possible
              </p>
            </motion.div>
            
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="bg-white/20 backdrop-blur-sm w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg"
                whileHover={{ scale: 1.1, rotate: 5 }}
              >
                <Brain className="text-white" size={40} />
              </motion.div>
              <h3 className="text-2xl font-bold mb-4">AI-Powered Intelligence</h3>
              <p className="text-white/80 leading-relaxed">
                Every product is enhanced with artificial intelligence to provide smarter, more adaptive solutions that learn and improve over time
              </p>
            </motion.div>
            
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="bg-white/20 backdrop-blur-sm w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg"
                whileHover={{ scale: 1.1, rotate: 5 }}
              >
                <Globe className="text-white" size={40} />
              </motion.div>
              <h3 className="text-2xl font-bold mb-4">Global Scale</h3>
              <p className="text-white/80 leading-relaxed">
                Built for global deployment with multi-language support, compliance standards, and infrastructure that scales worldwide
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;